import { useSupabaseQuery, useSupabaseMutation } from '@/hooks/useSupabaseQuery';
import { getSupabaseClient } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import type { Profile } from '@/lib/auth-types';

/**
 * 사용자 프로필을 관리하는 React Query 훅
 * 프로필이 없으면 자동으로 생성합니다.
 */
export const useProfile = (userId: string | null) => {
  const queryClient = useQueryClient();

  return useSupabaseQuery(
    ['profile', userId],
    async () => {
      if (!userId) return null;

      const supabase = getSupabaseClient();
      
      // 기존 프로필 조회
      const { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      // 프로필이 존재하면 반환
      if (existingProfile) {
        return existingProfile as Profile;
      }

      // 에러가 있고 'not found' 에러가 아닌 경우 throw
      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      // 프로필이 없으면 새로 생성
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: newProfile, error: createError } = await supabase
        .from('profiles')
        .insert({
          user_id: userId,
          email: user.email,
          name: user.user_metadata?.name || user.user_metadata?.full_name || null,
          avatar_url: user.user_metadata?.avatar_url || null,
          is_profile_complete: false
        })
        .select()
        .single();

      if (createError) {
        throw createError;
      }

      return newProfile as Profile;
    },
    {
      enabled: !!userId,
      staleTime: 5 * 60 * 1000, // 5분
      cacheTime: 10 * 60 * 1000, // 10분
    }
  );
};

/**
 * 프로필 업데이트를 위한 mutation 훅
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useSupabaseMutation(
    async ({ userId, updates }: { userId: string; updates: Partial<Profile> }) => {
      const supabase = getSupabaseClient();
      
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return data as Profile;
    },
    {
      onSuccess: (data) => {
        // 프로필 캐시 업데이트
        queryClient.setQueryData(['profile', data.user_id], data);
        queryClient.invalidateQueries(['profile', data.user_id]);
      },
    }
  );
};